import React, { useEffect, useRef, useState } from "react";
import { ModuleMesh, RoofMesh } from "../../../../@types/types";
import { Accordion, Toggle } from "../../../../components";
import { useViewerStore } from "../../../../store/store";
import { Badge, Button, Checkbox, Divider, Flex, InputNumber, Tooltip } from "antd";
import { AngleIcon, ArrowsHorizontalIcon, ArrowsOutCardinalIcon, ArrowsVerticalIcon, FlagIcon, PlusIcon, QuestionIcon, TrashIcon } from "@phosphor-icons/react";
import { addBallastedRackedModules, removeInvalidRackingsPanels, SetModuleSize, updateBallastedRackAngleForSelectedRoofs, updateBallastedRackAzimuthForSelectedRoofs } from "../../../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/ModuleControl";
import { isLightReachFinancePartner, syncLightReachSitePitchWarning } from "../../../../helpers/lightReachWarning";
import { AlertType, EditAction, ModuleOrientation } from "../../../../@types/enums";
import { MdTextRotationAngleup } from "react-icons/md";
import { useCurrentUser } from "../../../../contexts/UserContext";
import { registerRef } from "../../../../helpers/storeHelpers";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer.js";

const MIN_BALLAST_RACKING_ANGLE = 10;

interface RackingEditOptionsSectionProps {
    handleRemoveModule: (e: React.MouseEvent<HTMLElement>) => void;
    handleMoveModule: (e: React.MouseEvent<HTMLElement>) => void;
}

const RackingEditOptionSection: React.FC<RackingEditOptionsSectionProps> = (props: RackingEditOptionsSectionProps) => {

    const sceneViewer = useViewerStore(state => state.sceneViewer);
    const {hasCapability} = useCurrentUser();
    
    const isBallastedRackingMeeting25percentRule = useViewerStore((state) => state.isBallastedRackingMeeting25percentRule);
    const isBallastedRackTypeModuleActive = useViewerStore((state) => state.isBallastedRackTypeModuleActive);
    const isBallastedRackUpdated = useViewerStore((state) => state.isBallastedRackUpdated);
    const selRoofFaceArr = useViewerStore(state => state.selRoofFaceArr);
    const isBallastedAngleLabelVisible = useViewerStore(state => state.isBallastedAngleLabelVisible);
    const isMoveModuleActive = useViewerStore((state) => state.isMoveModuleActive)
    const pathname = useViewerStore(state => state.pathname);
    const [ballastedAzimuth, setBallastedAzimuth] = useState(0);
    const [ballastedAngle, setBallastedAngle] = useState(10);
    const removeInvalidAfterChangeRef = useRef(true);

    
    const addBallastedRackModule = () => {
        if (!Number.isFinite(ballastedAngle) || ballastedAngle < MIN_BALLAST_RACKING_ANGLE) {
            sceneViewer.viewerAlert({
                show: true,
                title: "Invalid Ballast Racking Angle",
                message: `Ballast racking angle must be at least ${MIN_BALLAST_RACKING_ANGLE}°.`,
                messageType: AlertType.error,
            });
            return;
        }
        const financePartnerName = useViewerStore.getState().financePartnerDetails?.name?.toLowerCase().trim() ?? "";
        const selectedRoofs = useViewerStore.getState().selRoofFaceArr || [];
        const selectedRoofList = selectedRoofs.length
            ? selectedRoofs
            : sceneViewer.roofMeshArr.filter((roof: RoofMesh) => roof.selectRoofFace !== false);
        const hasLowPitchSelectedRoof = selectedRoofList.some((roof: RoofMesh) => Number(roof?.userData?.pitchOverride ?? roof?.oriAng?.tilt ?? 0) <= 5);

        addBallastedRackedModules(ballastedAngle, ballastedAzimuth);

        if (isLightReachFinancePartner(financePartnerName) && hasLowPitchSelectedRoof) {
            syncLightReachSitePitchWarning(sceneViewer);
        }
    }

    const calculate25PercentBallastModules = () => {
        const nonBallastedModules = sceneViewer.moduleMeshArr.filter((item: ModuleMesh) => !item.userData.isBallastedRackType).length;
        //check if ballasted modules are 25% of non ballasted modules
        if (nonBallastedModules === 0) return "Min 10 Ballast modules required as No Regular modules added";

        return Math.round(nonBallastedModules * 0.25)
    }

    const setRackDirection = (newDirection: 'parallel' | 'vertical') => {

        let roofAz = selRoofFaceArr[0]?.oriAng?.azimuth ?? 0;
        
        if (newDirection === 'parallel') {
            roofAz = Math.round(roofAz > 180 ? roofAz - 90 : roofAz + 90);
        } else if (newDirection === 'vertical') {
            roofAz = Math.round(roofAz);
        }
        handleCustomAzimuthApply(roofAz, newDirection);
    };

    const handleCustomAzimuthApply = (val: number, direction?: 'parallel' | 'vertical') => {
        setBallastedAzimuth(val);
        const allBallastedModules = sceneViewer.moduleMeshArr.filter((m: any) => m.userData.isBallastedRackType && m.roofId === selRoofFaceArr[0].roofFaceId);
        if (allBallastedModules.length > 0) {
            sceneViewer.viewerAlert({
                show: true,
                title: "Confirm Azimuth Change",
                message: (
                    <>
                        <p>Change all existing ballasted modules to custom azimuth {val}°?</p>
                        <Checkbox
                            defaultChecked={true}
                            onChange={(e) => { removeInvalidAfterChangeRef.current = e.target.checked; }}
                        >
                            Remove invalid panels after change
                        </Checkbox>
                        <br />
                        <div className={"modal-bottom-action-button-container"}>
                            <Button
                                type="primary"
                                className="editor__control--finalize modal-confirm-button"
                                onClick={() => {
                                    // setCustomAzimuth(val);
                                    sceneViewer.viewerAlert({ show: false });
                                    applyOrientationChange(val, direction);
                                }}
                            >
                                Confirm
                            </Button>
                            <Button
                                className="editor__control--finalize modal-cancel-button"
                                onClick={() => {
                                    sceneViewer.viewerAlert({ show: false });
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    </>
                ),
                messageType: AlertType.info,
                isModal: true
            });
        } else {
            // setCustomAzimuth(val);
            selRoofFaceArr[0].userData.ballastedAzimuth = val;
            useViewerStore.setState({ activeBallastRackDirection: 'custom' });
            sceneViewer.ballastAzimuth = val;
            sceneViewer.ballastAngle = ballastedAngle;
        }
    };

    const applyOrientationChange = (
        azimuth: number,
        direction?: 'parallel' | 'vertical' | 'custom',
    ) => {
        const { sceneViewer, savedRoofModulesLayout } = useViewerStore.getState();
        const oldLayout = JSON.parse(JSON.stringify(savedRoofModulesLayout));

        const ballastedModules = sceneViewer.moduleMeshArr.filter((m: any) => m.userData.isBallastedRackType && m.roofId === selRoofFaceArr[0].roofFaceId);
        const ballastCount = ballastedModules.length;

        useViewerStore.setState({ activeBallastRackDirection: direction ?? 'custom', isBallastedRackTypeModuleActive: true });


        const newLayout = JSON.parse(JSON.stringify(useViewerStore.getState().savedRoofModulesLayout || []));
        if (ballastCount > 0) {
            sceneViewer.addStep(EditAction.ModuleLayout, 1, JSON.stringify(oldLayout), JSON.stringify(newLayout));
        }

        useViewerStore.setState({ savedRoofModulesLayout: newLayout });

        const dir = ModuleOrientation.Landscape;
        sceneViewer.ballastAngle = ballastedAngle;
        setBallastedAzimuth(azimuth);
        SetModuleSize(sceneViewer.virModule.children[0], useViewerStore.getState().activeModule, dir, true, true);
        selRoofFaceArr[0].userData.ballastedAzimuth = azimuth;

        updateBallastedRackAzimuthForSelectedRoofs(
            sceneViewer,
            selRoofFaceArr[0],
            azimuth,
            direction ?? 'custom',
        );

        if (removeInvalidAfterChangeRef.current !== false) {
            removeInvalidRackingsPanels(sceneViewer, selRoofFaceArr[0], { onlyBallasted: true });
        }

        sceneViewer.deselectAllSelectedObjectsAndEndTask();
    };

    const handleChangeBallastRackingAngle = (angle: number) => {
        if (!Number.isFinite(angle) || angle < MIN_BALLAST_RACKING_ANGLE) {
            sceneViewer.viewerAlert({
                show: true,
                title: "Invalid Ballast Racking Angle",
                message: `Ballast racking angle must be at least ${MIN_BALLAST_RACKING_ANGLE}°.`,
                messageType: AlertType.error,
            });
            return;
        }
        if (angle === ballastedAngle) return;
        const allBallastedModules = sceneViewer.moduleMeshArr.filter((m: any) => m.userData.isBallastedRackType);
        if (allBallastedModules.length > 0) {
            sceneViewer.viewerAlert({
                show: true,
                title: "Confirm Ballast Angle Change",
                message: (
                    <>
                        <p>Change all existing ballasted modules to {angle}°?</p>
                        <Checkbox
                            defaultChecked={true}
                            onChange={(e) => { removeInvalidAfterChangeRef.current = e.target.checked; }}
                        >
                            Remove invalid panels after change
                        </Checkbox>
                        <br />
                        <div className={"modal-bottom-action-button-container"}>
                            <Button
                                type="primary"
                                className="editor__control--finalize modal-confirm-button"
                                onClick={() => {
                                    sceneViewer.viewerAlert({ show: false });
                                    setBallastedAngle(angle);
                                    updateBallastedRackAngleForSelectedRoofs(sceneViewer, selRoofFaceArr[0], angle);
                                    if (removeInvalidAfterChangeRef.current) {
                                        removeInvalidRackingsPanels(sceneViewer, selRoofFaceArr[0], { onlyBallasted: true });
                                    }
                                    // sceneViewer.deselectAllSelectedObjectsAndEndTask();
                                }}
                            >
                                Confirm
                            </Button>
                            <Button
                                className="editor__control--finalize modal-cancel-button"
                                onClick={() => {
                                    sceneViewer.viewerAlert({ show: false });
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    </>
                ),
                messageType: AlertType.info,
                isModal: true
            });
        } else {
            // useViewerStore.setState({ ballastedRackModuleAngle: angle });
            // selRoofFaceArr[0].userData.ballastedPitch = angle;
            selRoofFaceArr.forEach((roof: RoofMesh) => {
                roof.userData.ballastedPitch = angle;
            });
            setBallastedAngle(angle);
            sceneViewer.ballastAngle = angle;
        }
    }

    const toggleBallastedAngleLabel = () => {
        const show = !isBallastedAngleLabelVisible;
        useViewerStore.setState({ isBallastedAngleLabelVisible: show });
        sceneViewer.moduleMeshArr.forEach((module: ModuleMesh) => {
            if (!module.userData.isBallastedRackType) return;
            module.children.forEach((child: any) => {
                if (child.name === "ballastAngleLabel") {
                    child.visible = !isBallastedAngleLabelVisible;
                    if (child.element) {
                        child.element.style.display = !isBallastedAngleLabelVisible ? '' : 'none';
                    }
                }
            });
        })
    }
    
    useEffect(() => {

        if(!selRoofFaceArr || selRoofFaceArr.length === 0) return;

        const roofBallastedAzimuth = selRoofFaceArr[0].userData.ballastedAzimuth ?? selRoofFaceArr[0].oriAng.azimuth ?? 0;
        const roofBallastedPitch = selRoofFaceArr[0].userData.ballastedPitch ?? ballastedAngle;
        setBallastedAzimuth(roofBallastedAzimuth);
        setBallastedAngle(roofBallastedPitch);

        // Auto-detect parallel vs perpendicular (landscape rack azimuth presets)
        const roofAz = Math.round(selRoofFaceArr[0].oriAng?.azimuth ?? 0);
        const normAz = ((Math.round(roofBallastedAzimuth) % 360) + 360) % 360;
        const normRoofAz = ((roofAz % 360) + 360) % 360;
        const normParallelAz = (normRoofAz + 90) % 360;
        if (normAz === normRoofAz) {
            useViewerStore.setState({ activeBallastRackDirection: 'vertical' });
        } else if (normAz === normParallelAz) {
            useViewerStore.setState({ activeBallastRackDirection: 'parallel' });
        } else {
            useViewerStore.setState({ activeBallastRackDirection: 'custom' });
        }

    }, [selRoofFaceArr, pathname])

    return (
        <div className={`tab__column makeBallastedRackingAccordianVisible`}>
            <Accordion id={"ballastRackingAccordian"} title='Ballasted Racking' active={true}>
                <Flex gap={10}>

                    <Button
                        icon={<PlusIcon size={18} />}
                        type='default'
                        size='middle'
                        className={`${isBallastedRackTypeModuleActive ? 'active' : ''}`}
                        id='addBallastRackBtn'
                        onClick={addBallastedRackModule}
                    >
                        Add
                    </Button>

                    {hasCapability('Edit_Module_Move') &&
                        <Button
                            ref={registerRef('moveModuleBtn')}
                            icon={<ArrowsOutCardinalIcon size={18}/>}
                            type='default'
                            size='middle'
                            id='moveModuleBtn'
                            className={`${isMoveModuleActive ? 'active' : ''}`}
                            onClick={props.handleMoveModule}
                            style={{width: "85px"}}
                        >Move</Button>
                    }

                    {
                        hasCapability('Edit_Module_Remove') &&
                            <Button
                                icon={ <TrashIcon size={18} />}
                                id={"moduleDeleteBtn"}
                                size='middle'
                                onClick={props.handleRemoveModule}
                                property='ModuleDel'
                                style={{width: "85px"}}
                            >Delete</Button>
                    }

                </Flex>
                <Divider />
                <span className='tab__column--title'>

                    {/* <ArrowsOutIcon /> */}
                    <AngleIcon />&nbsp;
                    Ballast Racking Azimuth</span> {/*Rack Direction*/}

                <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                    {/* {activeBallastRackDirection === 'custom' ? ( */}
                    <InputNumber
                        min={0}
                        max={360}
                        value={Math.round(ballastedAzimuth ?? 0)}
                        // onChange={(val) => setBallastedAzimuth(val as number ?? 0)}
                        onPressEnter={(e) => handleCustomAzimuthApply(Number((e.target as HTMLInputElement).value))}
                        onBlur={(e) => handleCustomAzimuthApply(Number(e.target.value))}
                        style={{ width: '100%' }}
                    />
                </div>

                <Flex gap={10}>

                    <Button
                        icon={<ArrowsHorizontalIcon size={18} />}
                        type={useViewerStore.getState().activeBallastRackDirection === 'parallel' ? 'primary' : 'default'}
                        size='middle'
                        id='setRackingParallelBtn'
                        onClick={() => setRackDirection('parallel')}
                    >
                        Parallel
                    </Button>                   
                    <Button
                        icon={<ArrowsVerticalIcon size={18}/>}
                        type={useViewerStore.getState().activeBallastRackDirection === 'vertical' ? 'primary' : 'default'}
                        size='middle'
                        id='setRackingVerticalBtn'
                        onClick={() => setRackDirection('vertical')}
                    >
                        Perpendicular
                    </Button>
                    

                </Flex>

                <Divider />

                <>
                    <span className='tab__column--title'><MdTextRotationAngleup size={18} />&nbsp;Ballast Racking Angle</span>
                    {

                        <InputNumber
                            min={MIN_BALLAST_RACKING_ANGLE}
                            max={90}
                            style={{ width: '100%' }}
                            value={ballastedAngle}
                            // onChange={(val) => {
                            //     const n = Number(val);
                            //     if (Number.isFinite(n) && n >= MIN_BALLAST_RACKING_ANGLE) {
                            //         setBallastedAngle(n);
                            //     }
                            // }}
                            onBlur={(e) => handleChangeBallastRackingAngle(Number(e.target.value))}
                            onPressEnter={(e) => handleChangeBallastRackingAngle(Number((e.target as any).value))}
                        />
                    }
                    <Divider />
                </>
                <div className='tab__flex justify-between'>
                    <span className='tab__column--subtitle mb-0'>Show Racking Angle Label (Pitch/Azimuth)</span>
                    <Toggle
                        on={isBallastedAngleLabelVisible}
                        onChange={toggleBallastedAngleLabel}
                    />
                </div>

                <Divider />



                <Flex align={"center"}>
                    <span className='tab__column--title'>
                        <FlagIcon />
                        &nbsp;
                        25% Rule
                        &nbsp;
                        <Tooltip title="
                              The 25% rule is a guideline that states that the number of ballast modules should be at least 25% of the number of regular modules (total system size).If this is only a flat roof design (only racking), then the minimum number of ballast modules should be 5.
                              ">
                            <QuestionIcon size={20} />
                        </Tooltip>

                    </span>
                </Flex>
                {isBallastedRackingMeeting25percentRule ?
                    <Badge status="success" text="Meeting 25% Rule" /> :
                    <Badge status="error" text="Not Meeting 25% Rule" />}
                <p className='tab__column--subtitle' data-updateon={isBallastedRackUpdated}>
                    Number of Pitched Regular Modules
                    Added: {sceneViewer.moduleMeshArr.filter((item: ModuleMesh) => !item.userData.isBallastedRackType).length}
                </p>
                <p className='tab__column--subtitle' data-updateon={isBallastedRackUpdated}>
                    Number of Ballast Modules
                    Added: {sceneViewer.moduleMeshArr.filter((item: ModuleMesh) => item.userData.isBallastedRackType).length}
                </p>
                <p className='tab__column--subtitle' data-updateon={isBallastedRackUpdated}>
                    25% Ballast
                    Modules: {calculate25PercentBallastModules()}
                </p>
                <p className='tab__column--subtitle' data-updateon={isBallastedRackUpdated}>

                </p>
            </Accordion>


        </div>
    )
}

export default RackingEditOptionSection;